import { useState, useEffect, useRef } from 'react';

interface StatProps {
  icon: string;
  value: number;
  suffix: string;
  label: string;
  color: string;
}

function AnimatedStat({ icon, value, suffix, label, color }: StatProps) {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;

    const duration = 2000;
    const steps = 60;
    const increment = value / steps;
    let current = 0;

    const timer = setInterval(() => {
      current += increment;
      if (current >= value) {
        setCount(value);
        clearInterval(timer);
      } else {
        setCount(Math.floor(current));
      }
    }, duration / steps);

    return () => clearInterval(timer);
  }, [isVisible, value]);

  return (
    <div ref={ref} className="flex flex-col items-center gap-2 group">
      <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${color} flex items-center justify-center shadow-lg group-hover:shadow-xl transition-all duration-300 group-hover:scale-110`}>
        <i className={`${icon} text-3xl text-white`}></i>
      </div>
      <div className="text-center">
        <div className="text-3xl md:text-4xl font-black text-gray-900">
          {count.toLocaleString()}{suffix}
        </div>
        <div className="text-sm md:text-base text-gray-600 font-medium mt-1">{label}</div>
      </div>
    </div>
  );
}

export default function TrustStatsBar() {
  return (
    <section className="py-16 bg-gradient-to-r from-orange-50 via-amber-50 to-rose-50">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12">
          <div className="flex flex-col items-center gap-2 group">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-500 to-rose-500 flex items-center justify-center shadow-lg group-hover:shadow-xl transition-all duration-300 group-hover:scale-110">
              <i className="ri-play-circle-fill text-3xl text-white"></i>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-black text-gray-900">Endless</div>
              <div className="text-sm md:text-base text-gray-600 font-medium mt-1">Cat Videos to Watch</div>
            </div>
          </div>

          <div className="flex flex-col items-center gap-2 group">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-rose-500 to-pink-500 flex items-center justify-center shadow-lg group-hover:shadow-xl transition-all duration-300 group-hover:scale-110">
              <i className="ri-heart-fill text-3xl text-white"></i>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-black text-gray-900">4M+</div>
              <div className="text-sm md:text-base text-gray-600 font-medium mt-1">Cats Adopted Each Year</div>
            </div>
          </div>

          <div className="flex flex-col items-center gap-2 group">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow-lg group-hover:shadow-xl transition-all duration-300 group-hover:scale-110">
              <i className="ri-shopping-bag-fill text-3xl text-white"></i>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-black text-gray-900">Endless</div>
              <div className="text-sm md:text-base text-gray-600 font-medium mt-1">Products for Your Cat</div>
            </div>
          </div>

          <div className="flex flex-col items-center gap-2 group">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-pink-500 to-rose-500 flex items-center justify-center shadow-lg group-hover:shadow-xl transition-all duration-300 group-hover:scale-110">
              <i className="ri-user-heart-fill text-3xl text-white"></i>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-black text-gray-900">Global</div>
              <div className="text-sm md:text-base text-gray-600 font-medium mt-1">Community of Cat Lovers</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
